import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Security,
  CheckCircle,
  Error,
  Preview,
  FileUpload,
  Close,
  Info,
  Visibility,
  Download,
  Delete,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';

interface DocumentUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadedFile {
  file: File;
  id: string;
}

interface ProcessingResult {
  document_analysis: {
    document_type: string;
    key_themes: string[];
    security_focus_areas: string[];
    compliance_frameworks: string[];
  };
  processing_metadata: {
    filename: string;
    file_size: number;
    document_type: string;
    text_length: number;
    processing_time_ms: number;
    rules_generated: number;
  };
  rules: any[];
  statistics: {
    rules_generated: number;
    rules_created?: number;
    rules_approved?: number;
    rules_pending?: number;
    processing_time_ms: number;
  };
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ open, onClose, onSuccess }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [documentType, setDocumentType] = useState<string>('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const documentTypes = [
    { value: 'hr_policy', label: 'HR Policy', description: 'Employee handbook, HR policies and procedures' },
    { value: 'nist', label: 'NIST Framework', description: 'NIST Cybersecurity Framework documents' },
    { value: 'fedramp', label: 'FedRAMP', description: 'Federal Risk and Authorization Management Program' },
    { value: 'security_policy', label: 'Security Policy', description: 'General security policies and procedures' },
    { value: 'compliance', label: 'Compliance Document', description: 'Regulatory compliance requirements' },
    { value: 'general_policy', label: 'General Policy', description: 'Other policy documents' },
  ];

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select a document to upload');
      return;
    }

    if (!documentType && !previewOnly) {
      toast.error('Please select a document type');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('document', files[0].file);
    
    if (documentType) {
      formData.append('document_type', documentType);
    }
    formData.append('auto_approve', autoApprove.toString());
    formData.append('generate_preview_only', previewOnly.toString());

    try {
      const endpoint = previewOnly ? '/api/documents/preview' : '/api/documents/upload';
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setProcessingResult(response.data);
      setShowPreview(true);

      if (!previewOnly) {
        toast.success(response.data.message);
        onSuccess();
      } else {
        toast.success('Document preview generated successfully');
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.error || 'Invalid file or request');
      } else if (error.response?.status === 413) {
        toast.error('File too large. Maximum size is 10MB');
      } else {
        toast.error('Failed to process document');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setDocumentType('');
    setAutoApprove(false);
    setPreviewOnly(false);
    setProcessingResult(null);
    setShowPreview(false);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'block': return 'error';
      case 'flag': return 'warning';
      case 'redact': return 'info';
      case 'modify': return 'primary';
      case 'alert': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FileUpload />
          Document Upload & Analysis
          <Tooltip title="Upload policy documents to automatically generate security rules">
            <Info color="action" fontSize="small" />
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!showPreview ? (
          <Grid container spacing={3}>
            {/* Upload Area */}
            <Grid item xs={12} md={8}>
              <Paper
                {...getRootProps()}
                sx={{
                  p: 4,
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  textAlign: 'center',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <input {...getInputProps()} />
                <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop the document here' : 'Drag & drop a document here'}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  or click to browse files
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supported formats: DOCX, PDF, TXT, MD (max 10MB)
                </Typography>
              </Paper>

              {files.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected File:
                  </Typography>
                  {files.map(({ file, id }) => (
                    <Paper key={id} sx={{ p: 2, mb: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Description color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton onClick={() => removeFile(id)} size="small">
                          <Close />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Configuration */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>

              <FormControl fullWidth margin="normal">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  label="Document Type"
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body2">{type.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={previewOnly}
                      onChange={(e) => setPreviewOnly(e.target.checked)}
                    />
                  }
                  label="Preview Only"
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  Generate rules preview without saving to database
                </Typography>
              </Box>

              {!previewOnly && (
                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoApprove}
                        onChange={(e) => setAutoApprove(e.target.checked)}
                      />
                    }
                    label="Auto-approve Rules"
                  />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Automatically approve and activate generated rules
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  The system will analyze your document and generate security rules based on the content.
                  HR policies will create employee data protection rules, NIST documents will create 
                  cybersecurity framework rules, etc.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        ) : (
          // Processing Results Preview
          <Box>
            <Typography variant="h6" gutterBottom>
              Processing Results
            </Typography>

            {processingResult && (
              <>
                {/* Document Analysis Summary */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Document Analysis
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Document Type
                        </Typography>
                        <Chip 
                          label={processingResult.document_analysis.document_type.replace('_', ' ').toUpperCase()} 
                          color="primary" 
                          size="small" 
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Processing Time
                        </Typography>
                        <Typography variant="body2">
                          {processingResult.statistics.processing_time_ms}ms
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Key Themes
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {processingResult.document_analysis.key_themes?.map((theme, index) => (
                            <Chip key={index} label={theme} size="small" variant="outlined" />
                          )) || []}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Security Focus Areas
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {processingResult.document_analysis.security_focus_areas?.map((area, index) => (
                            <Chip key={index} label={area} size="small" color="secondary" variant="outlined" />
                          )) || []}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Statistics */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Statistics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="h4" color="primary">
                          {processingResult.statistics.rules_generated}
                        </Typography>
                        <Typography variant="caption">
                          Rules Generated
                        </Typography>
                      </Grid>
                      {processingResult.statistics.rules_created !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="h4" color="success.main">
                            {processingResult.statistics.rules_created}
                          </Typography>
                          <Typography variant="caption">
                            Rules Created
                          </Typography>
                        </Grid>
                      )}
                      {processingResult.statistics.rules_approved !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="h4" color="info.main">
                            {processingResult.statistics.rules_approved}
                          </Typography>
                          <Typography variant="caption">
                            Auto-approved
                          </Typography>
                        </Grid>
                      )}
                      {processingResult.statistics.rules_pending !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="h4" color="warning.main">
                            {processingResult.statistics.rules_pending}
                          </Typography>
                          <Typography variant="caption">
                            Pending Approval
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Generated Rules */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Generated Security Rules
                    </Typography>
                    <List>
                      {processingResult.rules?.map((rule, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemIcon>
                              <Security />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1">
                                    {rule.name}
                                  </Typography>
                                  <Chip 
                                    label={rule.severity} 
                                    size="small" 
                                    color={getSeverityColor(rule.severity) as any}
                                  />
                                  <Chip 
                                    label={rule.action} 
                                    size="small" 
                                    color={getActionColor(rule.action) as any}
                                    variant="outlined"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {rule.description}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Pattern: {rule.pattern}
                                  </Typography>
                                  {rule.tags && rule.tags.length > 0 && (
                                    <Box display="flex" gap={0.5} mt={1}>
                                      {rule.tags?.map((tag: string, tagIndex: number) => (
                                        <Chip 
                                          key={tagIndex} 
                                          label={tag} 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ fontSize: '0.6rem', height: '20px' }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < (processingResult.rules?.length || 0) - 1 && <Divider />}
                        </React.Fragment>
                      )) || []}
                    </List>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        )}

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Processing document and generating security rules...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
        {!showPreview && (
          <>
            <Button
              variant="outlined"
              onClick={() => setPreviewOnly(true)}
              disabled={uploading || files.length === 0}
            >
              Preview Rules
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              startIcon={uploading ? undefined : <CloudUpload />}
            >
              {uploading ? 'Processing...' : 'Upload & Process'}
            </Button>
          </>
        )}
        {showPreview && previewOnly && (
          <Button
            variant="contained"
            onClick={() => {
              setPreviewOnly(false);
              setShowPreview(false);
              handleUpload();
            }}
            disabled={uploading}
          >
            Create Rules
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUpload;
